import axios from 'axios';
import { initializeApp } from 'firebase/app';

import {
  getAuth,
  EmailAuthProvider,
  signInWithCredential,
  signInWithCustomToken as firebaseSignInWithCustomToken,
  UserCredential,
  updateProfile,
  User,
  sendPasswordResetEmail,
  updatePassword,
  verifyPasswordResetCode,
  Auth,
  AuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  linkWithCredential,
  getRedirectResult,
  SAMLAuthProvider,
  OAuthProvider,
} from 'firebase/auth';

import { client } from '../apollo/ApolloConfig';
import environmentVariables, { initializeVariables } from '../appVariables';
import { publicGraphQLURL } from '../config';
import { refreshTokenService } from './auth';
import { GET_FIREBASE_TOKEN } from '../operations/mutations/public/useGetFirebaseToken/useGetFirebaseToken';
import firebaseConfig from '../views/Auth/Login/constant/firebase';
import { ILoginUserSuccessResponse } from '../views/Auth/Login/interfaces/IGoogleUser';

const appInitialize = (): Auth => {
  try {
    initializeVariables();
    initializeApp(firebaseConfig);

    return getAuth();
  } catch (error: any) {
    throw new Error(error.message);
  }
};

// Key used to remember, across the full-page navigation caused by `signInWithRedirect`, that a
// popup-based SSO login silently fell back to a redirect and is waiting for its result on the
// next mount. See `signInWithPopupOrRedirectFallback` and `LoginContent`'s `checkGoogleComplete`.
const SSO_REDIRECT_FALLBACK_STORAGE_KEY = 'ssoRedirectFallbackPending';

export const setSsoRedirectFallbackPending = (loginMethod: string): void => {
  try {
    sessionStorage.setItem(SSO_REDIRECT_FALLBACK_STORAGE_KEY, loginMethod);
  } catch {
    // sessionStorage can be unavailable (e.g. private browsing); the fallback stays best-effort.
  }
};

export const consumeSsoRedirectFallbackPending = (): string | null => {
  try {
    const pendingLoginMethod = sessionStorage.getItem(SSO_REDIRECT_FALLBACK_STORAGE_KEY);

    if (pendingLoginMethod) {
      sessionStorage.removeItem(SSO_REDIRECT_FALLBACK_STORAGE_KEY);
    }

    return pendingLoginMethod;
  } catch {
    return null;
  }
};

const isPopupDismissedByUser = (error: any): boolean =>
  Boolean(error?.message?.includes('popup-closed-by-user') || error?.message?.includes('auth/cancelled-popup-request'));

const isPopupBlockedByBrowser = (error: any): boolean => Boolean(error?.message?.includes('auth/popup-blocked'));

// Shared by every popup-based SSO provider (SAML, Google, Microsoft, Microsoft single-tenant):
// opens the Firebase popup and, if the browser silently blocked `window.open` (`auth/popup-blocked`,
// typically because Firebase's gapi/iframe warm-up pushed the call outside the user-gesture
// window), transparently falls back to a full-page `signInWithRedirect` instead of surfacing an
// error on this first attempt.
const signInWithPopupOrRedirectFallback = async (
  popupAuth: Auth,
  provider: AuthProvider,
  loginMethod: string,
): Promise<any> => {
  try {
    const user = await signInWithPopup(popupAuth, provider);

    return user;
  } catch (error: any) {
    if (isPopupDismissedByUser(error)) {
      return null;
    }

    if (isPopupBlockedByBrowser(error)) {
      setSsoRedirectFallbackPending(loginMethod);
      await signInWithRedirect(popupAuth, provider);

      return null;
    }

    throw error;
  }
};

export const loginGooglePopup = async (loginProvider?: SAMLAuthProvider): Promise<any> => {
  const provider = loginProvider ?? new GoogleAuthProvider();

  return signInWithPopupOrRedirectFallback(auth, provider, provider.providerId);
};

export const loginMicrosoftPopup = async (): Promise<any> => {
  const provider = new OAuthProvider('microsoft.com');

  return signInWithPopupOrRedirectFallback(auth, provider, provider.providerId);
};

export const loginMicrosoftSingleTenantPopup = async (): Promise<any> => {
  const provider = new OAuthProvider('microsoft.com');

  provider.setCustomParameters({
    tenant: environmentVariables?.ssoTenantID ?? '',
  });

  try {
    const user = await signInWithPopupOrRedirectFallback(auth, provider, provider.providerId);

    return user;
  } catch (error: any) {
    if (error.message.includes('auth/account-exists-with-different-credential')) {
      const microsoftCredential = OAuthProvider.credentialFromError(error);
      const email = error.customData?.email;

      if (!email) {
        throw new Error('No email found in account-exists error context');
      }

      if (!microsoftCredential) {
        throw new Error('No microsoft credential found in account-exists error context');
      }

      const idToken = microsoftCredential?.idToken;

      if (!idToken) {
        throw new Error('No idToken found in Microsoft credential');
      }

      try {
        const response = await client.mutate({
          mutation: GET_FIREBASE_TOKEN,
          variables: { email, token: idToken },
          context: { uri: publicGraphQLURL },
        });
        const customToken = response.data?.getFirebaseToken?.token;

        const userCredential = await firebaseSignInWithCustomToken(auth, customToken);

        await linkWithCredential(userCredential.user, microsoftCredential);

        return userCredential;
      } catch (mutationError: any) {
        throw new Error(mutationError.message);
      }
    }
    throw new Error(error.message);
  }
};

export const customSignInWithCustomToken = async (customToken: string): Promise<UserCredential> => {
  try {
    const auth: Auth = appInitialize();
    const userCredential: UserCredential = await firebaseSignInWithCustomToken(auth, customToken);

    return userCredential;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const firebaseAuth = async (email: string, password: string): Promise<UserCredential> => {
  try {
    const auth: Auth = appInitialize();
    const credentials = EmailAuthProvider.credential(email, password);
    const response: UserCredential = await signInWithCredential(auth, credentials);

    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const firebaseUpdateUser = async (): Promise<boolean> => {
  try {
    const currentUser: UserCredential = await firebaseAuth('cnh@ualberta.ca', 'B7BGemedByT4Jydg');

    await updateProfile(currentUser.user as User, {
      displayName: 'Chau Hoang',
    });

    return true;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const firebaseSendPasswordResetEmail = async (email: string): Promise<boolean> => {
  try {
    initializeApp(firebaseConfig);
    const auth: Auth = getAuth();

    await sendPasswordResetEmail(auth, email);

    return true;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const changePassword = async (password: string): Promise<boolean> => {
  try {
    const { currentUser } = getAuth();

    if (!currentUser) {
      return false;
    }

    await updatePassword(currentUser, password);

    return true;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const changePasswordVerifyCode = async (code: string): Promise<string> => {
  try {
    const auth: Auth = appInitialize();
    const response: string = await verifyPasswordResetCode(auth, code);

    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const resetPassword = async (oobCode: string, newPassword: string, key: string): Promise<boolean> => {
  try {
    await axios.post(
      `https://identitytoolkit.googleapis.com/v1/accounts:resetPassword?key=${key}`,
      {
        oobCode,
        newPassword,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    return true;
  } catch (error: any) {
    throw new Error(error.message);
  }
};
// 45 min
const DURATION_TOKEN_MILLISECONDS = 45 * 60 * 1000;

export const refreshToken = async (auth: ILoginUserSuccessResponse): Promise<ILoginUserSuccessResponse> => {
  let token: ILoginUserSuccessResponse = auth;

  await axios
    .post(
      `https://securetoken.googleapis.com/v1/token?key=${firebaseConfig.apiKey}`,
      new URLSearchParams({
        // eslint-disable-next-line camelcase
        grant_type: 'refresh_token',
        // eslint-disable-next-line camelcase
        refresh_token: token.refreshToken,
      }),
    )
    .then(async ({ data }) => {
      if (data && data.access_token && data.refresh_token) {
        token = {
          ...token,
          refreshToken: data.refresh_token,
          expiresIn: Date.now() + DURATION_TOKEN_MILLISECONDS,
        };
        if (auth && auth?.jwtToken) {
          token = await refreshTokenService(auth?.jwtToken, data.refresh_token);
        }
      }
    });

  return token;
};

export const loginSaml = async (authProvider: string): Promise<any> => {
  const samlProvider = new SAMLAuthProvider(authProvider);

  return signInWithPopupOrRedirectFallback(auth, samlProvider, samlProvider.providerId);
};

const auth: Auth = appInitialize();

export const redirectResultGoogle = async (): Promise<any> => {
  try {
    return await getRedirectResult(auth);
  } catch (error: any) {
    throw new Error(error.message);
  }
};
